// Persistent left-rail navigation for the admin shell.
//
// Replaces the old per-view <ViewSwitcher> that used to live in the top-right
// of every page header. Keeping navigation on the left side gives us a single,
// always-visible, icon-first entry point for the three top-level views and
// frees the header for page-specific actions (sync, concerns, filters…).
//
// Layout contract (consumed by App.tsx):
//   <div className="flex h-screen">
//     <AppSidebar current=... onNavigate=... theme=... onToggleTheme=... />
//     <main className="flex-1 min-w-0">{view}</main>
//   </div>
//
// The rail is a fixed narrow column (w-16) on desktop. Each nav item is an
// icon with a tooltip; the active item gets a solid indigo pill + left
// accent bar so the current view is unmistakable at a glance. The theme
// toggle lives at the bottom so it's out of the way but always reachable.

import {
    Layers,
    FolderKanban,
    ClipboardList,
    Moon,
    Sun,
    ShieldCheck,
    LogOut,
    type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../lib/auth';

export type SidebarView = 'planning' | 'project-details' | 'tasks' | 'access';

interface AppSidebarProps {
    current: SidebarView;
    onNavigate: (view: SidebarView) => void;
    theme: 'light' | 'dark';
    onToggleTheme: () => void;
    // Disable the Project Details rail item if no project is selected.
    disableProjectDetails?: boolean;
    // Only shown to users holding users.manage.
    showAccess?: boolean;
}

interface NavItem {
    value: SidebarView;
    label: string;
    Icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
    { value: 'planning',        label: 'Planning',        Icon: Layers },
    { value: 'project-details', label: 'Project Details', Icon: FolderKanban },
    { value: 'tasks',           label: 'Tasks',           Icon: ClipboardList },
];

const ACCESS_ITEM: NavItem = { value: 'access', label: 'User Access', Icon: ShieldCheck };

export default function AppSidebar({
    current,
    onNavigate,
    theme,
    onToggleTheme,
    disableProjectDetails = false,
    showAccess = false,
}: AppSidebarProps) {
    const { user, signOut } = useAuth();
    const navItems = showAccess ? [...NAV_ITEMS, ACCESS_ITEM] : NAV_ITEMS;

    return (
        <aside
            className="flex flex-col items-center w-16 flex-shrink-0 bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800 py-3"
            aria-label="Primary navigation"
        >
            <div className="w-10 h-10 mb-4 flex items-center justify-center" title="Rohan">
                <img src="/rohan-logo.svg" alt="Rohan" className="w-9 h-9 object-contain brightness-0 dark:brightness-100" />
            </div>

            <nav className="flex-1 w-full flex flex-col items-center gap-1" role="tablist">
                {navItems.map(({ value, label, Icon }) => {
                    const isActive = current === value;
                    const isDisabled = value === 'project-details' && disableProjectDetails;
                    return (
                        <button
                            key={value}
                            role="tab"
                            aria-selected={isActive}
                            aria-label={label}
                            title={label}
                            disabled={isDisabled}
                            onClick={() => onNavigate(value)}
                            className={[
                                'relative group w-12 h-12 flex items-center justify-center rounded-lg transition-colors',
                                isActive
                                    ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100'
                                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white',
                                isDisabled ? 'opacity-40 cursor-not-allowed hover:bg-transparent' : '',
                            ].join(' ')}
                        >
                            {/* Active indicator: 3px bar on the left edge */}
                            {isActive && (
                                <span
                                    aria-hidden
                                    className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-0.5 rounded-r bg-neutral-800 dark:bg-neutral-400"
                                />
                            )}
                            <Icon size={20} />
                            {/* Hover tooltip (pure CSS, no extra deps) */}
                            <span
                                className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-gray-900 text-white text-xs font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-50"
                            >
                                {label}
                            </span>
                        </button>
                    );
                })}
            </nav>

            {/* Bottom cluster: theme toggle and sign out */}
            <button
                onClick={onToggleTheme}
                title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
                aria-label="Toggle theme"
                className="w-12 h-12 flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
                {theme === 'light' ? <Moon size={18} /> : <Sun size={18} className="text-yellow-400" />}
            </button>

            <button
                onClick={signOut}
                title={user ? `Sign out (${user.username ?? user.name})` : 'Sign out'}
                aria-label="Sign out"
                className="group relative w-12 h-12 flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
                <LogOut size={18} />
                <span className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-gray-900 text-white text-xs font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-50">
                    {user ? `Sign out · ${user.username ?? user.name}` : 'Sign out'}
                </span>
            </button>
        </aside>
    );
}
